import { configureStore, isPlain } from "@reduxjs/toolkit";
import { TypedUseSelectorHook, useDispatch, useSelector } from "react-redux";

import authReducer from "features/auth/authSlice";
import clientViewReducer from "features/clientView/clientViewSlice";
import notificationReducer from "features/notificationSlice";
import folderConnectSyncReducer from "features/folderConnectSyncSlice";
import topFolderZipReducer, { hydrateTopFolderZip } from "features/topFolderZipSlice";
import { hydrateBrowserZip } from "features/browserZipSlice";
import {
  persistTopFolderZipState,
  loadTopFolderZipState,
} from "features/topFolderZipPersistence";
import {
  persistBrowserZipState,
  loadBrowserZipState,
} from "features/browserZipPersistence";
import browserZipReducer from "features/browserZipSlice";
import pdfSplitJobsReducer, {
  hydratePdfSplitJobs,
} from "features/pdfSplitJobsSlice";
import {
  persistPdfSplitJobsState,
  loadPdfSplitJobsState,
} from "features/pdfSplitJobsPersistence";
import { apiWithAuth } from "features/auth/auth";
import api from "./api";
import listenerMiddleware from "middleware/listenerMiddleware";

const authMiddleware = (store: any) => (next: any) => (action: any) => {
  if (apiWithAuth.endpoints.login.matchFulfilled(action)) {
    localStorage.setItem("accessToken", action.payload.access);
    localStorage.setItem("refreshToken", action.payload.refresh);
  } else if (apiWithAuth.endpoints.logout.matchFulfilled(action)) {
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
  }
  return next(action);
};

function customIsSerializable(value: unknown): boolean {
  if (value instanceof Blob) return true;
  return isPlain(value);
}

export const store = configureStore({
  reducer: {
    auth: authReducer,
    clientView: clientViewReducer,
    notification: notificationReducer,
    folderConnectSync: folderConnectSyncReducer,
    topFolderZip: topFolderZipReducer,
    browserZip: browserZipReducer,
    pdfSplitJobs: pdfSplitJobsReducer,
    [api.reducerPath]: api.reducer,
  },
  devTools: process.env.NODE_ENV !== "production",
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        isSerializable: customIsSerializable,
      },
    }).concat(authMiddleware, listenerMiddleware.middleware, api.middleware),
});

let _prevTopFolderZipSerial = "";
let _prevBrowserZipSerial = "";
let _prevPdfSplitJobsSerial = "";
let _prevAuthUidForZip: number | null = null;

store.subscribe(() => {
  const s = store.getState();
  const uid = s.auth.user?.id;
  if (uid == null) {
    _prevAuthUidForZip = null;
    return;
  }
  const uidN = Number(uid);

  if (_prevAuthUidForZip !== uidN) {
    _prevAuthUidForZip = uidN;
    // Read every persisted slice BEFORE the first hydrate dispatch: dispatching
    // re-enters this subscriber, which persists the still-empty slices and
    // would otherwise wipe the not-yet-loaded storage entries.
    const tf = loadTopFolderZipState();
    const bz = loadBrowserZipState();
    const psj = loadPdfSplitJobsState();
    if (tf && Number(tf.userId) === uidN) {
      store.dispatch(hydrateTopFolderZip({ jobs: tf.jobs, ready: tf.ready }));
    }
    if (bz && Number(bz.userId) === uidN) {
      store.dispatch(hydrateBrowserZip(bz.jobs));
    }
    if (psj && Number(psj.userId) === uidN) {
      store.dispatch(hydratePdfSplitJobs(psj.jobs));
    }
  }

  const s2 = store.getState();
  const tfzSerial = JSON.stringify({
    j: s2.topFolderZip.jobs,
    r: s2.topFolderZip.ready,
  });
  if (tfzSerial !== _prevTopFolderZipSerial) {
    _prevTopFolderZipSerial = tfzSerial;
    persistTopFolderZipState(uidN, s2.topFolderZip);
  }

  const bzSerial = JSON.stringify({ j: s2.browserZip.jobs });
  if (bzSerial !== _prevBrowserZipSerial) {
    _prevBrowserZipSerial = bzSerial;
    persistBrowserZipState(uidN, s2.browserZip.jobs);
  }

  const psjSerial = JSON.stringify({ j: s2.pdfSplitJobs.jobs });
  if (psjSerial !== _prevPdfSplitJobsSerial) {
    _prevPdfSplitJobsSerial = psjSerial;
    persistPdfSplitJobsState(uidN, s2.pdfSplitJobs.jobs);
  }
});

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;

export const useAppDispatch = () => useDispatch<AppDispatch>();
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector;
